import {
	Button,
	DataTable,
	type DataTableColumn,
	EmptyState,
	Input,
	PageHeader,
	Stack,
	Text,
	Toolbar,
} from "@app/ui"
import { Tag } from "@app/ui/next"
import { useQuery } from "@tanstack/react-query"
import { Link, createRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import {
	type PartySummary,
	exportPartiesCsv,
	listParties,
	partiesQueryKeys,
} from "../../api/parties"
import ExportCsvButton from "../../components/ExportCsvButton"
import { humanizeStatus } from "../../lib/format"
import { HttpError } from "../../lib/http"
import { useScope } from "../../scope/scope-context"
import { Route as RootRoute } from "../__root"

/**
 * Parties tenant-wide list -- `/parties`.
 *
 * Mirrors `/lots`: PageHeader + Toolbar (count + search) + DataTable with
 * the canonical five-state scaffold. A search input re-keys the react-query
 * cache per term. Parties have no chronological ordering worth surfacing,
 * so the list is alphabetical by name.
 */
export const Route = createRoute({
	getParentRoute: () => RootRoute,
	path: "/parties",
	component: PartiesIndexPage,
})

const COMPANY_LINE = "All parties in your tenant, alphabetically. Search by name or party ID."

export function PartiesIndexPage() {
	const navigate = useNavigate()
	const { params: scope } = useScope()
	// Parties scope is country-only (decision 5): vertical is dropped here so a
	// vertical-only scope change neither re-keys nor refetches the list.
	const partyScope = scope.country !== undefined ? { country: scope.country } : {}
	const [q, setQ] = useState("")
	const trimmedQ = q.trim()
	const query = useQuery({
		queryKey: partiesQueryKeys.list(trimmedQ, partyScope),
		queryFn: ({ signal }) =>
			listParties({ ...(trimmedQ.length > 0 ? { q: trimmedQ } : {}), scope: partyScope, signal }),
	})

	const rows = query.data ?? []
	const countLabel = query.isSuccess
		? `${rows.length} ${rows.length === 1 ? "party" : "parties"}`
		: "Parties"

	const columns: DataTableColumn<PartySummary>[] = [
		{
			key: "name",
			header: "Name",
			cell: (party) => (
				// Native link inside the row: preserves cmd/middle-click + prefetch +
				// keyboard focus while the whole row also navigates via onRowClick.
				<Link
					to="/parties/$partyId"
					params={{ partyId: party.id }}
					onClick={(e) => e.stopPropagation()}
				>
					{party.name}
				</Link>
			),
		},
		{ key: "role", header: "Role", cell: (party) => <Tag>{humanizeStatus(party.role)}</Tag> },
		{ key: "country", header: "Country", mono: true, cell: (party) => party.countryCode ?? "—" },
		{ key: "locode", header: "Port", mono: true, cell: (party) => party.unlocode ?? "—" },
	]

	return (
		<Stack gap="5" align="stretch">
			<PageHeader
				title="Parties"
				subtitle={COMPANY_LINE}
				actions={
					<ExportCsvButton
						base="parties"
						scope={scope}
						disabled={rows.length === 0}
						fetchCsv={() =>
							exportPartiesCsv({ ...(trimmedQ.length > 0 ? { q: trimmedQ } : {}), scope })
						}
					/>
				}
			/>

			<Toolbar lead={<Text variant="label">{countLabel}</Text>}>
				<Input
					type="search"
					inputSize="sm"
					placeholder="Search name or party id…"
					value={q}
					onChange={(e) => setQ(e.target.value)}
				/>
				{q.length > 0 ? (
					<Button variant="secondary" size="sm" onClick={() => setQ("")}>
						Clear
					</Button>
				) : null}
			</Toolbar>

			{query.isError ? (
				<ErrorState error={query.error} onRetry={() => query.refetch()} />
			) : (
				<DataTable
					columns={columns}
					rows={rows}
					getRowKey={(party) => party.id}
					isLoading={query.isPending}
					onRowClick={(party) =>
						navigate({ to: "/parties/$partyId", params: { partyId: party.id } })
					}
					empty={<EmptyStateBody hasQuery={trimmedQ.length > 0} />}
				/>
			)}
		</Stack>
	)
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const message =
		error instanceof HttpError
			? `HTTP ${error.status}`
			: error instanceof Error
				? error.message
				: "Unknown error"
	return (
		<Stack gap="3" align="start">
			<Text variant="heading-sm" tone="danger">
				Couldn't load parties
			</Text>
			<Text variant="body-sm" tone="muted">
				{message}
			</Text>
			<Button variant="secondary" size="sm" onClick={onRetry}>
				Retry
			</Button>
		</Stack>
	)
}

function EmptyStateBody({ hasQuery }: { hasQuery: boolean }) {
	return (
		<EmptyState
			title={hasQuery ? "No matches" : "No parties yet"}
			description={
				hasQuery
					? "Try a different name or party ID."
					: "Parties seeded by the engine will appear here."
			}
		/>
	)
}
